import { jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useMemo, createContext, useContext, useCallback, useLayoutEffect } from 'react';
import clsx from 'clsx';
import { useForwardFocusToInput } from '../hooks/use-forward-focus-to-input.js';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { textareaReset } from './textarea-reset.css.js';

/**
 * Allows placing elements "inside" the Textarea. Must contain a single `TextareaPrimitive` element.
 */
const Root = forwardRef(({ children, onPointerDown: onPointerDownIn, ...props }, ref) => {
    const [disabled, setDisabled] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const ctx = useMemo(() => ({ setDisabled, setReadOnly }), []);
    const onPointerDown = useForwardFocusToInput(onPointerDownIn);
    return (jsx(TextareaContext.Provider, { value: ctx, children: jsx("div", { "data-fpl-component": "primitive", ...props, ref: ref, onPointerDown: onPointerDown, "data-fpl-disabled": disabled || undefined, "data-fpl-readonly": readOnly || undefined, children: children }) }));
});
Root.displayName = 'TextareaPrimitive.Root';
const TextareaContext = createContext(null);
/**
 * A simple wrapper over a native textarea element.
 *
 * @see https://fpl.figma.design/primitives/textarea/
 */
const TextareaPrimitive = Object.assign(
// eslint-disable-next-line react/display-name -- False positive because of the Object.assign
forwardRef(({ className, disabled, expandable, maxHeight, onChange, recordingKey, rows = 3, spellCheck = true, style, readOnly, ...props }, refIn) => {
    const ctx = useContext(TextareaContext);
    const ref = useSharedRef(refIn);
    const handleChange = useCallback((...args) => {
        onChange?.(...args);
        if (expandable && ref.current) {
            resizeHeight(ref.current);
        }
    }, [expandable, onChange, ref]);
    const recordedOnChange = useInteractionHandler(handleChange, { eventName: 'change', recordingKey }, [handleChange]);
    useLayoutEffect(() => {
        if (expandable && ref.current) {
            resizeHeight(ref.current);
        }
        else if (!expandable && ref.current) {
            ref.current.style.height = '';
        }
    }, [expandable, ref]);
    useLayoutEffect(() => {
        ctx?.setDisabled(disabled ?? false);
        ctx?.setReadOnly(readOnly ?? false);
    }, [ctx, disabled, readOnly]);
    return (jsx("textarea", { "data-fpl-component": "primitive", ...props, rows: rows, className: clsx(textareaReset, className), style: { ...style, maxHeight }, ref: ref, onChange: recordedOnChange ? (e) => recordedOnChange(e.target.value, { event: e }) : undefined, 
        // Instead of using the disabled attribute, set readonly so that it can still be focused.
        readOnly: readOnly || disabled, "aria-disabled": disabled || undefined, spellCheck: spellCheck }));
}), { Root });
TextareaPrimitive.displayName = 'TextareaPrimitive';
function resizeHeight(el) {
    el.style.height = 'auto';
    if (el.scrollHeight > 0) {
        el.style.height = `${el.scrollHeight}px`;
    }
}

export { TextareaPrimitive };
//# sourceMappingURL=textarea-primitive.js.map
